/**
 * Las llamadas concretas que hace el panel.
 *
 * Cada funcion corresponde a **un endpoint que hoy existe y devuelve datos
 * reales**. No hay mocks ni endpoints inventados: si una pantalla necesitara
 * algo que el backend no expone, la pantalla no se hace.
 *
 * Rutas verificadas contra `src/api/v1/router.py` (2026-08-26).
 */

import type { ApiClient, RequestOptions } from "./client";
import type { ProductProfitability, Store, StoreStats } from "./types";

/**
 * Listar tiendas.
 *
 * ADVERTENCIA DE TENENCIA: este endpoint **no filtra por usuario**. Esta
 * anotado como deuda conocida en `src/api/deps.py::UNSCOPED_LIST_ENDPOINTS`
 * ("GET /stores"), porque no recibe `store_id` y el chequeo generico de
 * tenencia se dispara solo cuando el request nombra una tienda.
 *
 * Por eso el selector NO usa esta lista tal cual: la usa como conjunto de
 * candidatos y confirma cada una contra el servidor. Ver
 * `src/stores/StoreAccessProvider.tsx`.
 */
export function listStores(client: ApiClient, options?: RequestOptions): Promise<Store[]> {
  return client.get<Store[]>("/stores", options ?? {});
}

/**
 * Traer una tienda por id.
 *
 * `store_id` viaja en el path, asi que `enforce_authentication` aplica tenencia:
 * 200 = concedida, 403 = denegada. Esa es justamente la senal que usa el
 * selector para saber que puede ver el usuario.
 */
export function getStore(
  client: ApiClient,
  storeId: number,
  options?: RequestOptions,
): Promise<Store> {
  return client.get<Store>(`/stores/${storeId}`, options ?? {});
}

/** Estadisticas de una tienda (productos, campanas, historial). Store-scoped. */
export function getStoreStats(
  client: ApiClient,
  storeId: number,
  options?: RequestOptions,
): Promise<StoreStats> {
  return client.get<StoreStats>(`/stores/${storeId}/stats`, options ?? {});
}

/**
 * Productos mas rentables.
 *
 * ADVERTENCIA DE TENENCIA: tambien esta en `UNSCOPED_LIST_ENDPOINTS`. Devuelve
 * productos de TODAS las tiendas porque no recibe `store_id`. La pantalla de
 * rentabilidad lo dice explicitamente en la UI en vez de fingir que el filtro
 * de tienda aplica.
 */
export function getTopProfitableProducts(
  client: ApiClient,
  limit: number,
  options?: RequestOptions,
): Promise<ProductProfitability[]> {
  return client.get<ProductProfitability[]>("/pricing/top-profitable", {
    ...options,
    query: { limit },
  });
}

/** Productos por debajo de un margen minimo. Misma advertencia que el anterior. */
export function getLowMarginProducts(
  client: ApiClient,
  minMargin: number,
  options?: RequestOptions,
): Promise<ProductProfitability[]> {
  return client.get<ProductProfitability[]>("/pricing/low-margin", {
    ...options,
    query: { min_margin: minMargin },
  });
}

/**
 * Respuesta de `GET /health/ready`.
 *
 * Este es el UNICO tipo escrito a mano en todo el cliente, y es a proposito:
 * el handler de `src/api/v1/health.py` devuelve un `JSONResponse` crudo sin
 * `response_model`, asi que el OpenAPI lo declara como `{}` y el generador no
 * tiene de donde sacar la forma. La deuda es del backend (declarar un
 * `response_model` ahi), no del generador; queda anotada en el README.
 */
export interface ReadinessCheck {
  readonly name: string;
  readonly status: string;
  readonly code?: string;
  readonly missing_count?: number;
}

export interface ReadinessResponse {
  readonly status: "ready" | "degraded" | "not_ready";
  readonly version: string;
  readonly checks: readonly ReadinessCheck[];
}

/**
 * Readiness de la API.
 *
 * Es un endpoint publico (`src/api/deps.py::_PUBLIC_API_ENDPOINTS`): responde
 * sin token. Por eso sirve para distinguir "la API esta caida" de "mi sesion no
 * sirve", que desde el navegador se parecen bastante.
 */
export function getReadiness(
  client: ApiClient,
  options?: RequestOptions,
): Promise<ReadinessResponse> {
  return client.get<ReadinessResponse>("/health/ready", options ?? {});
}
